import {ActionEvent, Controller} from '@hotwired/stimulus';
import {addEventListener, createHTMLElementFromTemplate} from 'app/utils/dom';
import {autoUpdate, computePosition, flip, offset, Placement, shift} from '@floating-ui/dom';
import {appendToRoot, Root} from 'app/utils/element-placer';
import {Typed} from "app/stimulus/typed-stimulus";
import {AsStimulusController} from "app/stimulus/as-stimulus-controller";
import {ResolvableController} from "app/stimulus/resolvable-controller";
import overlayStack from "app/service/overlay-stack";
import {Component} from "@symfony/ux-live-component";
import {onLiveComponentRender} from "app/stimulus/live-component-render";
import {
    closeWithNestedOverlayAfterAction,
    findCloseOnClickElement,
    findMenuItem,
    focusWithoutScroll,
    getFallbackPlacements,
    getMenuItems,
    isElementVisible,
    isFocusInside,
    isFocusInsideOrLost,
    isInsideOverlayTree,
    isKeyboardActivation,
    isPopoverOpen,
    isTextEntry,
    isToggleCloseEvent,
    OverlayClosedDetail,
    resolveLiveComponent,
    setPanelOwner,
    triggersLiveAction,
    waitForLiveRequest,
} from "app/stimulus/overlay-panel";

const controllerName = 'dropdown';

const closeOnClickAttribute = 'data-dropdown-close-on-click';

type InitialFocus = 'none' | 'first' | 'last';

/**
 * Dropdown panel rendered from the `template` target into a native `popover="manual"` element: the panel
 * lives in the top layer (no clipping by `overflow`, no z-index fights) while staying in the DOM of its
 * `root`, so Live actions inside it keep working. Positioned by floating-ui with the fixed strategy and kept
 * in place by `autoUpdate`; outside click and Escape (topmost overlay only) close it. When an overlay opened
 * from the panel (e.g. a Confirm popover of an item) closes after its Live action, the dropdown closes too:
 * the Live render does not update the JS-appended panel.
 *
 * A panel with menu items (`role="menu"`) has the menu keyboard model: opening from the keyboard focuses the
 * first item, ArrowDown/ArrowUp (also on the trigger), Home and End move between the enabled items and Tab
 * closes the menu. Closing it from the keyboard or from an item returns the focus to the trigger.
 */
@AsStimulusController(controllerName)
export default class DropdownController extends Typed(
    ResolvableController(Controller<HTMLElement>, controllerName), {
        targets: {
            template: HTMLTemplateElement,
        },
        values: {
            removeOnHide: {type: Boolean, default: false},
            placement: {type: String, default: 'bottom-start'},
            root: {type: String, default: 'body'},
            stopPropagation: {type: Boolean, default: false},
            closeOnEvents: Array<string>,
        },
        classes: [
            'isOpen',
        ] as const,
    }
)
{
    private dataElement: HTMLElement | null = null;
    private dataElementListeners: AbortController | null = null;
    private isOpen: boolean = false;
    private stopAutoUpdate: (() => void) | null = null;
    private liveComponent: Component | null = null;
    private openCount: number = 0;
    private removeRenderListener: (() => void) | null = null;

    public connect(): void {
        this.syncTriggerAria();
        // A Live render puts the server attributes of the trigger back, which may lack these two.
        this.removeRenderListener = onLiveComponentRender(this.element, (): void => this.syncTriggerAria());
    }

    public disconnect(): void {
        this.removeRenderListener?.();
        this.removeRenderListener = null;
        this.closeImmediately();
        this.destroyDataElement();
    }

    public isOpened(): boolean {
        return this.isOpen;
    }

    /**
     * The panel element, or null before the first open (and after a close with `removeOnHide`).
     */
    public getDropdown(): HTMLElement | null {
        return this.dataElement;
    }

    public async open(): Promise<void> {
        await this.show('none');
    }

    private async show(initialFocus: InitialFocus): Promise<void> {
        if (this.isOpen) {
            return;
        }
        const dropdown = this.getDataElement();
        this.dispatch('opening');
        dropdown.style.visibility = 'hidden';
        dropdown.style.position = 'fixed';
        dropdown.style.left = '0px';
        dropdown.style.top = '0px';
        if (!isPopoverOpen(dropdown)) {
            dropdown.showPopover();
        }
        this.isOpen = true;
        const openCount = ++this.openCount;
        overlayStack.register(this);
        this.element.setAttribute('aria-expanded', 'true');
        dropdown.removeAttribute('aria-hidden');
        this.element.classList.add(this.isOpenClass);
        dropdown.classList.add(this.isOpenClass);
        await this.computePosition();
        // A close (and possibly another open) during the measurement makes this open stale.
        if (!this.isOpen || this.dataElement !== dropdown || openCount !== this.openCount) {
            return;
        }
        dropdown.style.visibility = '';
        this.stopPositionUpdates();
        this.stopAutoUpdate = autoUpdate(this.element, dropdown, (): void => {
            this.computePosition();
        });
        resolveLiveComponent(dropdown).then((component: Component | null): void => {
            this.liveComponent = component;
        });
        this.dispatch('opened');
        this.focusInitialItem(dropdown, initialFocus);
    }

    public async close(): Promise<void> {
        this.closeImmediately();
    }

    public async toggle(event: ActionEvent | null = null): Promise<void> {
        // With `root: 'toggler'` the panel lives inside the trigger, so clicks inside it bubble to the trigger.
        if (this.dataElement !== null && event !== null && event.target instanceof Node && this.dataElement.contains(event.target)) {
            return;
        }
        if (this.isOpen) {
            this.closeImmediately(isKeyboardActivation(event));
        } else {
            await this.show(isKeyboardActivation(event) ? 'first' : 'none');
        }
    }

    public async computePosition(): Promise<void> {
        const dropdown = this.dataElement;
        if (!this.isOpen || dropdown === null) {
            return;
        }
        if (!this.element.isConnected || !isElementVisible(this.element)) {
            this.closeImmediately();
            return;
        }
        const placement = (this.placementValue !== '' ? this.placementValue : 'bottom-start') as Placement;
        const position = await computePosition(this.element, dropdown, {
            placement: placement,
            strategy: 'fixed',
            middleware: [offset(5), flip({crossAxis: false, fallbackPlacements: getFallbackPlacements(placement)}), shift({padding: 5})],
        });
        if (!this.isOpen || this.dataElement !== dropdown) {
            return;
        }
        dropdown.style.left = `${position.x}px`;
        dropdown.style.top = `${position.y}px`;
    }

    public onGlobalClick(event: Event): void {
        if (!this.isOpen || this.dataElement === null) {
            return;
        }
        if (isInsideOverlayTree(event.target, [this.element, this.dataElement])) {
            return;
        }
        this.closeImmediately();
    }

    public handleKeydown(event: KeyboardEvent): void {
        if (event.defaultPrevented) {
            return;
        }
        if (!this.isOpen) {
            this.openFromKeyboard(event);
            return;
        }
        if (!overlayStack.isTopmost(this)) {
            return;
        }
        if (event.key === 'Escape') {
            // Marks the key press as handled, so the overlay below does not close on the same press.
            event.preventDefault();
            this.closeImmediately(true);
            return;
        }
        this.handleMenuKeydown(event);
    }

    /**
     * ArrowDown/ArrowUp on a menu trigger opens the menu with the focus on its first/last item. Triggers inside
     * another overlay's panel are left to that overlay's menu navigation.
     */
    private openFromKeyboard(event: KeyboardEvent): void {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
            return;
        }
        if (event.target !== this.element || isTextEntry(this.element) || this.element.getAttribute('aria-haspopup') !== 'menu') {
            return;
        }
        if (this.element.closest('[popover]') !== null) {
            return;
        }
        event.preventDefault();
        void this.show(event.key === 'ArrowDown' ? 'first' : 'last');
    }

    private handleMenuKeydown(event: KeyboardEvent): void {
        const dropdown = this.dataElement;
        if (dropdown === null || dropdown.querySelector('[role="menu"]') === null) {
            return;
        }
        const currentItem = findMenuItem(event.target, dropdown);
        const onTrigger = event.target === this.element;
        if (currentItem === null && !onTrigger) {
            return;
        }
        if (event.key === 'Tab') {
            // The focus returns to the trigger and the Tab key moves on from there.
            this.closeImmediately(true);
            return;
        }
        const items = getMenuItems(dropdown);
        if (items.length === 0) {
            return;
        }
        const index = currentItem === null ? -1 : items.indexOf(currentItem);
        let next: HTMLElement | null = null;
        if (event.key === 'ArrowDown') {
            next = items[(index + 1) % items.length];
        } else if (event.key === 'ArrowUp') {
            next = index <= 0 ? items[items.length - 1] : items[index - 1];
        } else if (event.key === 'Home' && currentItem !== null) {
            next = items[0];
        } else if (event.key === 'End' && currentItem !== null) {
            next = items[items.length - 1];
        }
        if (next === null) {
            return;
        }
        event.preventDefault();
        next.focus();
    }

    private focusInitialItem(dropdown: HTMLElement, initialFocus: InitialFocus): void {
        if (initialFocus === 'none') {
            return;
        }
        const items = getMenuItems(dropdown);
        if (items.length === 0) {
            return;
        }
        (initialFocus === 'first' ? items[0] : items[items.length - 1]).focus();
    }

    /**
     * Hiding the panel loses the focus inside it, so the focus goes back to the trigger; `returnFocus` also
     * returns a focus that is already lost (the user closed the panel by keyboard or from an item).
     * `afterAction` marks a close after the Live action of a close-on-click element (`dropdown:closed` detail).
     */
    private closeImmediately(returnFocus: boolean = false, afterAction: boolean = false): void {
        if (!this.isOpen) {
            return;
        }
        const focusTrigger = this.dataElement !== null
            && (returnFocus ? isFocusInsideOrLost(this.dataElement) : isFocusInside(this.dataElement));
        this.dispatch('closing');
        this.isOpen = false;
        overlayStack.unregister(this);
        this.stopPositionUpdates();
        this.element.setAttribute('aria-expanded', 'false');
        this.element.classList.remove(this.isOpenClass);
        const dropdown = this.dataElement;
        if (dropdown !== null) {
            if (isPopoverOpen(dropdown)) {
                dropdown.hidePopover();
            }
            dropdown.style.visibility = '';
            dropdown.setAttribute('aria-hidden', 'true');
            dropdown.classList.remove(this.isOpenClass);
            if (this.removeOnHideValue) {
                this.destroyDataElement();
            }
        }
        if (focusTrigger && this.element.isConnected) {
            focusWithoutScroll(this.element);
        }
        const detail: OverlayClosedDetail = {afterAction: afterAction};
        this.dispatch('closed', {detail: detail});
    }

    private closeAfterClick(event: Event): void {
        const dropdown = this.dataElement;
        if (dropdown === null) {
            return;
        }
        const marked = findCloseOnClickElement(event.target, dropdown, closeOnClickAttribute);
        if (marked === null) {
            return;
        }
        const component = this.liveComponent;
        if (component !== null && triggersLiveAction(event.target, marked)) {
            // Keeps the item (and its loader) in the DOM until the Live request ends.
            const openCount = this.openCount;
            waitForLiveRequest(component).then((): void => {
                if (openCount === this.openCount) {
                    this.closeImmediately(true, true);
                }
            });
            return;
        }
        setTimeout((): void => this.closeImmediately(true));
    }

    /**
     * Announces the menu on the trigger (unless the markup sets its own `aria-haspopup`) and its current state.
     */
    private syncTriggerAria(): void {
        if (!this.element.hasAttribute('aria-haspopup')) {
            this.element.setAttribute('aria-haspopup', 'menu');
        }
        this.element.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');
    }

    private stopPositionUpdates(): void {
        if (this.stopAutoUpdate !== null) {
            this.stopAutoUpdate();
            this.stopAutoUpdate = null;
        }
    }

    private destroyDataElement(): void {
        this.dataElementListeners?.abort();
        this.dataElementListeners = null;
        this.liveComponent = null;
        if (this.dataElement !== null) {
            this.dataElement.remove();
            this.dataElement = null;
        }
    }

    private getDataElement(): HTMLElement {
        if (this.dataElement !== null) {
            return this.dataElement;
        }
        const element = createHTMLElementFromTemplate(this.templateTarget);
        const listeners = new AbortController();
        element.setAttribute('aria-hidden', 'true');
        // Capture phase: a `:stop` modifier on the clicked Live action must not prevent the close.
        element.addEventListener('click', (event: MouseEvent): void => this.closeAfterClick(event), {capture: true, signal: listeners.signal});
        closeWithNestedOverlayAfterAction(element, (): void => this.closeImmediately(true, true), listeners.signal);
        element.addEventListener('click', (event: MouseEvent): void => {
            if (this.stopPropagationValue) {
                event.stopPropagation();
            }
        }, {signal: listeners.signal});
        // Keeps the state in sync when something else hides the native popover.
        element.addEventListener('toggle', (event: Event): void => {
            if (isToggleCloseEvent(event) && this.isOpen && this.dataElement === element) {
                this.closeImmediately();
            }
        }, {signal: listeners.signal});
        if (this.hasCloseOnEventsValue && this.closeOnEventsValue.length > 0) {
            for (const eventName of this.closeOnEventsValue) {
                addEventListener(element, eventName, (): void => {
                    setTimeout((): void => this.closeImmediately());
                }, {signal: listeners.signal});
            }
        }
        appendToRoot(this.rootValue as Root, element, this.element);
        setPanelOwner(element, this.element);
        this.dataElement = element;
        this.dataElementListeners = listeners;
        this.dispatch('dropdown-created', {detail: {dropdown: element}});
        return element;
    }
}
